import { useMemo } from 'react';

const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ukDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  hour12: false,
});

const formatOrdinal = (value: number) => {
  const remainder10 = value % 10;
  const remainder100 = value % 100;
  if (remainder10 === 1 && remainder100 !== 11) return `${value}st`;
  if (remainder10 === 2 && remainder100 !== 12) return `${value}nd`;
  if (remainder10 === 3 && remainder100 !== 13) return `${value}rd`;
  return `${value}th`;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const getUkNowParts = (now: Date) => {
  const parts = ukDateTimeFormatter.formatToParts(now);
  const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  const year = Number(getPart('year'));
  const month = Number(getPart('month'));
  const dayOfMonth = Number(getPart('day'));
  const hour = Number(getPart('hour'));
  const ukDate = new Date(Date.UTC(year, month - 1, dayOfMonth));

  return {
    ukDate,
    hour,
  };
};

const isUkClinicianAvailabilityWindow = (now: Date) => {
  const { ukDate, hour } = getUkNowParts(now);
  const day = ukDate.getUTCDay();
  const isWeekday = day >= 1 && day <= 5;
  return isWeekday && hour >= 9 && hour < 17;
};

const computeDeliveryDate = (now: Date) => {
  const { ukDate, hour } = getUkNowParts(now);
  const day = ukDate.getUTCDay();
  const isWeekend = day === 0 || day === 6;
  const isAfterCutoff = hour >= 15;

  if (!isWeekend && !isAfterCutoff) {
    return addDays(ukDate, 1);
  }

  if (day === 5 && isAfterCutoff) {
    return addDays(ukDate, 4);
  }

  if (day === 6) {
    return addDays(ukDate, 3);
  }

  if (day === 0) {
    return addDays(ukDate, 2);
  }

  return addDays(ukDate, 2);
};

const AvailabilityBanner = () => {
  const isClinicianAvailable = useMemo(() => isUkClinicianAvailabilityWindow(new Date()), []);
  const deliveryCopy = useMemo(() => {
    const deliveryDate = computeDeliveryDate(new Date());
    const weekday = weekdayNames[deliveryDate.getUTCDay()];
    const dayWithOrdinal = formatOrdinal(deliveryDate.getUTCDate());
    return { weekday, dayWithOrdinal };
  }, []);

  return (
    <div className="w-full bg-[#FF9F9B]">
      <div
        className="mx-auto flex max-w-screen-2xl flex-col items-center justify-center gap-1 px-4 py-2 text-center text-sm font-normal text-gray-900 sm:h-9 sm:flex-row sm:gap-0"
        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
      >
        {isClinicianAvailable && (
          <span className="hidden items-center justify-center gap-2 sm:mr-0 sm:flex">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500 status-glow" aria-hidden />
            <span className="tracking-tight">
              Clinicians are available now to review your answers
              <span className="hidden sm:inline mx-2">|</span>
            </span>
          </span>
        )}
        <span className="sm:ml-0">
          Complete now to get delivery by{' '}
          <span className="font-semibold">
            {deliveryCopy.weekday} {deliveryCopy.dayWithOrdinal}
          </span>
        </span>
      </div>
    </div>
  );
};

export default AvailabilityBanner;
